import { useQuery } from "@tanstack/react-query";
import * as publicFlowApi from "@/lib/api/public-flow/flow-instance";
import { useSessionToken } from "@/lib/context/public-session-context";

/**
 * Hooks for the public flow-instance split endpoints (S2 of the
 * `/public/flow/instance` lightening workstream). Each one consumes the
 * dedicated backend endpoint added in S1.
 *
 * `staleTime` choices:
 *   - definition → 5 minutes. Flow definitions change only when a designer
 *     edits the flow; a public session is unlikely to span such an edit. The
 *     advisor side uses `Infinity` here, but public sessions are longer-lived
 *     (clients open the link, leave, come back) so we leave a refresh window
 *     to catch edits during long idle periods.
 *   - client → 5 minutes. Doesn't mutate during a public flow; advisor edits
 *     to client info during a long-running public session are uncommon.
 *   - tenant → 30 minutes. Tenant branding changes very rarely (logo, theme).
 *     Aggressive caching is safe.
 */

const keys = {
  definition: () => ["publicFlow", "definition"] as const,
  client: () => ["publicFlow", "client"] as const,
  tenant: () => ["publicFlow", "tenant"] as const,
};

const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

export function usePublicFlowDefinition() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.definition(),
    queryFn: () => publicFlowApi.getPublicFlowDefinition(token),
    enabled: !!token,
    staleTime: FIVE_MINUTES,
  });
  return { definition: data ?? null, error, isLoading };
}

export function usePublicFlowClient() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.client(),
    queryFn: () => publicFlowApi.getPublicFlowClient(token),
    enabled: !!token,
    staleTime: FIVE_MINUTES,
  });
  return { client: data ?? null, error, isLoading };
}

export function usePublicFlowTenant() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.tenant(),
    queryFn: () => publicFlowApi.getPublicFlowTenant(token),
    enabled: !!token,
    staleTime: THIRTY_MINUTES,
    // The logo URLs are presigned for an hour (QL-429); refresh them in time.
    refetchInterval: THIRTY_MINUTES,
  });
  return { tenant: data ?? null, error, isLoading };
}

export const publicFlowInstanceQueryKeys = keys;
